import React, { useState } from 'react';

import type { ToolbarSelectProps } from 'src/components';
import { CheckMarkMenuItem, PermissionRoleInput, ToolbarSelect } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { getStorableRoleLabel } from 'src/helpers';
import { z } from 'zod';

const styleSelectAttribute = 'link-share-permission-select-style';
const styleSpanAttribute = 'link-share-permission-select-style-span';
const styleMenuAttribute = 'link-share-permission-style-menu';

const PermissionRoleSchema = z.enum(Apollo.PermissionRole);

export const permissionRoleToDisplayNameMap = {
  [Apollo.PermissionRole.Viewer]: 'View',
  [Apollo.PermissionRole.Commenter]: 'Comment',
  [Apollo.PermissionRole.Suggester]: 'Suggest',
  [Apollo.PermissionRole.Editor]: 'Edit',
};

interface PermissionRoleMenuProps extends Omit<
  ToolbarSelectProps,
  'open' | 'menuAttribute' | 'selectAttribute' | 'spanAttribute'
> {
  handleSelect: (role: Apollo.PermissionRole) => void;
  value: Apollo.PermissionRole | null;
  border?: boolean;
  className?: string;
  disabled?: boolean;
  enabledRoles?: Apollo.PermissionRole[];
  noValueText?: string;
}

export function PermissionRoleMenu({
  border = true,
  className = '',
  disabled,
  enabledRoles = [
    Apollo.PermissionRole.Viewer,
    Apollo.PermissionRole.Commenter,
    Apollo.PermissionRole.Suggester,
    Apollo.PermissionRole.Editor,
  ],
  handleSelect,
  noValueText = 'Disabled',
  value,
  ...rest
}: PermissionRoleMenuProps): JSX.Element | null {
  const [permissionsOpen, setPermissionsOpen] = useState<boolean>(false);

  if (!enabledRoles.length) return null;

  return (
    <ToolbarSelect
      aria-label="Permissions"
      border={border}
      className={className}
      customRenderValue={value ? permissionRoleToDisplayNameMap[value] : noValueText}
      disabled={disabled}
      input={<PermissionRoleInput open={permissionsOpen} />}
      menuAttribute={styleSelectAttribute}
      open={permissionsOpen}
      selectAttribute={styleMenuAttribute}
      spanAttribute={styleSpanAttribute}
      value={value}
      onClose={() => setPermissionsOpen(false)}
      onOpen={() => setPermissionsOpen(true)}
      onSelectChange={async event => {
        const parsedValue = PermissionRoleSchema.parse(event.target.value);
        await handleSelect(parsedValue);
      }}
      {...rest}
    >
      {enabledRoles.map((role, index) => (
        <CheckMarkMenuItem key={index} selected={value === role} value={role}>
          {`Can ${getStorableRoleLabel(role)}`}
        </CheckMarkMenuItem>
      ))}
    </ToolbarSelect>
  );
}
